/**
 * Department Service
 * Handles department management operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class DepartmentService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? DepartmentService.DEFAULT_SERVICE_PATH,
            basePath: DepartmentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    async getAll(isActive) {
        const response = await this.getAllRaw(isActive);
        return response.data ?? [];
    }
    getAllRaw(isActive) {
        const params = isActive !== undefined ? { isActive } : {};
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Department with ID ${id} not found`);
        }
        return response.data;
    }
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== MODEL VIEWS ====================
    async getModelView(id) {
        const response = await this.getModelViewRaw(id);
        if (!response.data) {
            throw new Error(`Department ModelView with ID ${id} not found`);
        }
        return response.data;
    }
    getModelViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    async getAllModelViews() {
        const response = await this.getAllModelViewsRaw();
        return response.data ?? [];
    }
    getAllModelViewsRaw() {
        const url = this.buildUrl('view');
        return this.http.get(url);
    }
    // ==================== DEPARTMENT MEMBERS ====================
    async getDepartmentMembers(departmentId) {
        const response = await this.getDepartmentMembersRaw(departmentId);
        return response.data ?? [];
    }
    getDepartmentMembersRaw(departmentId) {
        const url = this.buildUrl(`${departmentId}/members`);
        return this.http.get(url);
    }
    async addMember(departmentId, memberId, data) {
        const response = await this.addMemberRaw(departmentId, memberId, data);
        return response.success;
    }
    addMemberRaw(departmentId, memberId, data) {
        const url = this.buildUrl(`${departmentId}/members/${memberId}`);
        return this.http.post(url, data);
    }
    async removeMember(departmentId, memberId) {
        const response = await this.removeMemberRaw(departmentId, memberId);
        return response.success;
    }
    removeMemberRaw(departmentId, memberId) {
        const url = this.buildUrl(`${departmentId}/members/${memberId}`);
        return this.http.delete(url);
    }
    // ==================== CREATE ====================
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create department');
        }
        return response.data;
    }
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update department with ID ${id}`);
        }
        return response.data;
    }
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== CACHE ====================
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
DepartmentService.DEFAULT_SERVICE_PATH = 'departments';
DepartmentService.BASE_PATH = 'api/departments';
//# sourceMappingURL=DepartmentService.js.map